/**
 * Anexa UTMs, session_id, parâmetros de rastreamento (trk/trk2) e dados do
 * lead a uma URL de checkout (Eduzz), preservando qualquer query existente.
 *
 * - UTMs → repassadas pra Eduzz e devolvidas no postback (atribuição).
 * - session_id → amarra a venda à sessão de origem no nosso banco.
 * - trk / trk2 → parâmetros injetados pelo GTM na URL do embed
 *   (também aceitos como `tracker_trk`/`tracker_trk2`). Fallbacks:
 *     - trk: cookie `index` (fisioemortopedia.com.br) → localStorage `index`
 *     - trk2: utm_term
 * - Dados do lead (nome/email/telefone) → prefil do checkout Eduzz, para
 *   a pessoa não precisar digitar de novo. Parâmetros suportados pela
 *   Eduzz: `name`, `email`, `phone` (e alias `phone_number`).
 */
export type CheckoutCustomer = {
  nome?: string;
  email?: string;
  telefone?: string;
};

function onlyDigits(v: string) {
  return v.replace(/\D+/g, "");
}

function readTrackingIds(utms: Record<string, string | null | undefined>): {
  trk: string;
  trk2: string;
} {
  let trk = "";
  let trk2 = "";
  if (typeof window !== "undefined") {
    try {
      const qs = new URLSearchParams(window.location.search);
      trk = qs.get("trk") || qs.get("tracker_trk") || "";
      if (!trk) {
        const m = document.cookie.match(/(?:^|;\s*)index=([^;]+)/);
        if (m) trk = decodeURIComponent(m[1]);
      }
      if (!trk) {
        try {
          trk = window.localStorage.getItem("index") || "";
        } catch {
          /* ignore */
        }
      }
      trk2 =
        qs.get("trk2") ||
        qs.get("tracker_trk2") ||
        qs.get("utm_term") ||
        "";
    } catch {
      /* ignore */
    }
  }
  if (!trk2) {
    const term = utms.utm_term;
    if (term) trk2 = term;
  }
  return { trk, trk2 };
}

export function appendUtmsToCheckoutUrl(
  url: string,
  utms: Record<string, string | null | undefined>,
  sessionId?: string,
  customer?: CheckoutCustomer,
): string {
  try {
    const u = new URL(url);
    const set = (k: string, v: string | null | undefined) => {
      if (v && !u.searchParams.has(k)) u.searchParams.set(k, v);
    };
    set("utm_source", utms.utm_source);
    set("utm_medium", utms.utm_medium);
    set("utm_campaign", utms.utm_campaign);
    set("utm_content", utms.utm_content);
    set("utm_term", utms.utm_term);

    const { trk, trk2 } = readTrackingIds(utms);
    if (trk) set("trk", trk);
    if (trk2) set("trk2", trk2);

    if (sessionId && !u.searchParams.has("tracker_sid")) {
      u.searchParams.set("tracker_sid", sessionId);
    }
    if (customer) {
      const nome = customer.nome?.trim();
      const email = customer.email?.trim();
      const tel = customer.telefone ? onlyDigits(customer.telefone) : "";
      if (nome) {
        set("name", nome);
      }
      if (email) {
        set("email", email);
      }
      if (tel) {
        // Eduzz aceita `phone`; alguns fluxos ainda usam `phone_number`.
        set("phone", tel);
        set("phone_number", tel);
      }
    }
    return u.toString();
  } catch {
    return url;
  }
}
